import React, { createContext, useState, useEffect, useContext } from 'react';

const DarkModeContext = createContext(null);

export const DarkModeProvider = ({ children, storageKey = 'darkMode' }) => {
   const [darkMode, setDarkMode] = useState(() => {
      // Check localStorage first
      const stored = localStorage.getItem(storageKey);
      if (stored !== null) {
         return stored === 'true';
      }
      // Then check system preference
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
   });

   useEffect(() => {
      // Apply dark mode class to document
      if (darkMode) {
         document.documentElement.classList.add('dark');
      } else {
         document.documentElement.classList.remove('dark');
      }
      // Save to localStorage
      localStorage.setItem(storageKey, darkMode.toString());
   }, [darkMode, storageKey]);

   // Listen for system preference changes (only if user hasn't set a preference)
   useEffect(() => {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e) => {
         // Only update if user hasn't manually set a preference
         const stored = localStorage.getItem(storageKey);
         if (stored === null) {
            setDarkMode(e.matches);
         }
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
   }, [storageKey]);

   const toggleDarkMode = () => {
      setDarkMode((prev) => !prev);
   };

   const value = {
      darkMode,
      toggleDarkMode,
      setDarkMode,
   };

   return <DarkModeContext.Provider value={value}>{children}</DarkModeContext.Provider>;
};

export const useDarkMode = () => {
   const context = useContext(DarkModeContext);
   if (!context) {
      throw new Error('useDarkMode must be used within a DarkModeProvider');
   }
   return context;
};
